import { useEffect, useRef, useState } from 'react'
import { APP_SCROLL_EVENT } from './scrollEvents'

function parseBottomMargin(margin) {
  const match = String(margin || '').match(/(-?\d+(?:\.\d+)?)%/)
  if (!match) return 0.08
  return Math.abs(Number(match[1])) / 100
}

function viewportHeight() {
  return window.innerHeight || document.documentElement.clientHeight || 0
}

/** Elemento entrou na zona de reveal (base do viewport). */
export function isElementRevealed(el, margin = '0px 0px -5% 0px') {
  if (!el) return false

  const rect = el.getBoundingClientRect()
  const vh = viewportHeight()
  if (rect.bottom <= 0 || rect.top >= vh) return false

  const bottomInset = parseBottomMargin(margin)
  const triggerLine = vh * (1 - bottomInset)

  return rect.top < triggerLine
}

/** Qualquer parte visível no viewport. */
export function isElementPartiallyVisible(el) {
  if (!el) return false
  const rect = el.getBoundingClientRect()
  const vh = viewportHeight()
  return rect.bottom > 0 && rect.top < vh
}

/** Elemento saiu completamente do viewport — pode resetar para animar de novo. */
export function isElementFullyOutOfView(el, buffer = 32) {
  if (!el) return false

  const rect = el.getBoundingClientRect()
  const vh = viewportHeight()

  return rect.bottom <= -buffer || rect.top >= vh + buffer
}

const subscribers = new Set()
let rafId = 0
let listenersBound = false

function runAllSubscribers() {
  subscribers.forEach((fn) => fn())
}

function startLoop() {
  if (rafId || subscribers.size === 0) return
  const loop = () => {
    if (subscribers.size === 0) {
      rafId = 0
      return
    }
    runAllSubscribers()
    rafId = requestAnimationFrame(loop)
  }
  rafId = requestAnimationFrame(loop)
}

function stopLoop() {
  if (!rafId) return
  cancelAnimationFrame(rafId)
  rafId = 0
}

function onScrollActivity() {
  runAllSubscribers()
  startLoop()
}

function bindGlobalListeners() {
  if (listenersBound || typeof window === 'undefined') return
  listenersBound = true

  const opts = { passive: true, capture: true }
  window.addEventListener('scroll', onScrollActivity, opts)
  document.addEventListener('scroll', onScrollActivity, opts)
  window.addEventListener('wheel', onScrollActivity, opts)
  window.addEventListener('touchmove', onScrollActivity, opts)
  window.addEventListener('keydown', onScrollActivity, opts)
  window.addEventListener('resize', onScrollActivity, opts)
  window.addEventListener(APP_SCROLL_EVENT, onScrollActivity, opts)
  window.addEventListener('pageshow', onScrollActivity)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) onScrollActivity()
  })
}

function subscribe(fn) {
  bindGlobalListeners()
  subscribers.add(fn)
  fn()
  startLoop()
  return () => {
    subscribers.delete(fn)
    if (subscribers.size === 0) stopLoop()
  }
}

/**
 * Reveal ao scroll — repete sempre que o elemento entra no viewport.
 * IO nativo + scroll/wheel/teclas + rAF contínuo (desktop Windows incluído).
 */
export function useRevealInView(options = {}) {
  const { once = false, margin = '0px 0px -5% 0px' } = options
  const ref = useRef(null)
  const lockedRef = useRef(false)
  const revealedRef = useRef(false)
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined

    const reveal = () => {
      if (once && lockedRef.current) return
      if (once) lockedRef.current = true
      revealedRef.current = true
      setRevealed(true)
    }

    const hide = () => {
      if (once) return
      revealedRef.current = false
      setRevealed(false)
    }

    const update = (intersecting = null) => {
      if (once && lockedRef.current) return

      const inZone =
        intersecting === true ||
        (intersecting !== false && isElementRevealed(el, margin))

      if (inZone) {
        reveal()
        return
      }

      if (!once && isElementFullyOutOfView(el)) {
        hide()
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => update(entry.isIntersecting))
      },
      {
        root: null,
        rootMargin: margin,
        threshold: [0, 0.01, 0.05, 0.1, 0.2, 0.35, 0.5, 0.75, 1],
      }
    )
    observer.observe(el)

    const unsubscribe = subscribe(() => update(null))

    const safety = window.setInterval(() => {
      if (once && lockedRef.current) return
      if (!revealedRef.current && isElementPartiallyVisible(el)) {
        reveal()
      }
    }, 250)

    return () => {
      window.clearInterval(safety)
      unsubscribe()
      observer.disconnect()
    }
  }, [margin, once])

  return { ref, revealed }
}
